import React, { useState } from "react";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { Button } from "react-bootstrap";
import dayjs from "dayjs";
import { useGetCompanies, useGetCompanyLocations } from "../../../backend/masters";
import { DEFAULT_OPTIONS_PAYLOAD } from "../../common/Table";
import { GetComplianceBreadcrumb } from "./Compliance.constants";
import { useExportComplianceSchedule, useImportComplianceSchedule } from "../../../backend/compliance";
import { download, getMaxMonthYear, getMinMonthYear, readBlobAsJson } from "../../../utils/common";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../common/FormRenderer";
import { ActivityType } from "../Masters/Master.constants";
import { API_DELIMITER, ERROR_MESSAGES } from "../../../utils/constants";
import MastersLayout from "../Masters/MastersLayout";
import PageLoader from "../../shared/PageLoader";
import ComplianceScheduleImportModal from "./ComplianceScheduleImportModal";
import BaseImportModal from "../../common/BaseImportModal";
import { EXCEL_FILE_REGEX, FILE_SIZE } from "../../common/Constants";
import { AxiosError } from "axios";
import { toast } from "react-toastify";

const DEFAULT_EXPORT_DATA = {
    hideButtons: true,
    month: new Date(),
    types: ['Display', 'Registers', 'Returns'].map(x => {
        return { value: x, label: x }
    })
}

function ComplianceSchedule(this: any) {
    const [form, setForm] = useState<any>({});
    const [importFile, setImportFile] = useState(false);
    const [breadcrumb] = useState(GetComplianceBreadcrumb('Compliance Schedule'));
    const [exportData, setExportData] = useState<any>({ ...DEFAULT_EXPORT_DATA });
    const [parentCompany, setParentCompany] = useState<any>(null);
    const [associate_company, setAssociateCompany] = useState<any>(null);
    const [selectedState, setSelectedState] = useState<any>(null);
    const [formKey, setFormKey] = useState(0);
    const { companies, isFetching: fetchingCompanies } = useGetCompanies({ ...DEFAULT_OPTIONS_PAYLOAD, filters: {is_parent_eq: true, is_active_eq: true} });
    const { companies: associateCompanies, isFetching: fetchingAssociateCompanies } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {is_parent_eq: false, is_active_eq: true, parent_company_id_eq: (parentCompany || {}).value }
    }, Boolean(parentCompany));
    const { locations, isFetching: fetchingLocations } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD, filters: {company_id_eq: (associate_company || {}).value },
        sort: ['location_name asc'],
        include: { location: ['city', 'state'] },
        includes: { location: ['city', 'state'] }
    }, Boolean(associate_company));
    const { exportComplianceSchedule, exporting } = useExportComplianceSchedule(async (response: any) => {
        if(response instanceof AxiosError){
            const contentType = response.response && response.response.headers['content-type'];
            if (contentType.includes('application/json')) {
                const errorJson =  await readBlobAsJson((response.response || {}).data || {});
                let errors = (errorJson || {}).errors|| [ERROR_MESSAGES.DEFAULT];
                toast.error(errors.join(', '));
            }
        } else {
            const blob = new Blob([response.data], { type: response.headers['content-type'] })
            const URL = window.URL || window.webkitURL;
            const downloadUrl = URL.createObjectURL(blob);
            download(getFileName(), downloadUrl);
        }
    }, () => {

    });

    const schema = {
        fields: [
            {
                component: componentTypes.SELECT,
                name: 'parentCompany',
                label: 'Company',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                options: companies,
                isLoading: fetchingCompanies,
                onChange: onParentCompanyChange.bind(this),
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'associate_company',
                label: 'Associate Company',
                options: associateCompanies,
                isDisabled: !Boolean(parentCompany),
                isLoading: fetchingAssociateCompanies,
                onChange: onAssociateCompanyChange.bind(this),
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'state',
                label: 'State',
                isDisabled: !Boolean(associate_company),
                options: (() => {
                    const unique: any = {};
                    (locations || []).forEach((x: any) => {
                        const { state } = x.location || {};
                        if (state && !unique[state.id]) {
                            unique[state.id] = { value: state.id, label: state.name };
                        }
                    });
                    return Object.values(unique);
                })(),
                isClearable: true,
                onChange: (state: any) => {
                    setSelectedState(state || null);
                    setExportData((prev: any) => ({ ...prev, state, locations: null }));
                    setFormKey((k: number) => k + 1);
                },
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'locations',
                label: 'Locations',
                options: (locations || [])
                    .filter((x: any) => !selectedState || ((x.location || {}).state || {}).id === selectedState.value)
                    .map((x: any) => {
                        const { id, name, code, city, state }: any = x.location || {};
                        return {
                            id,
                            name: `${name} (${state.code}-${city.code}-${code})`,
                            code: `${state.code}-${city.code}-${code}`
                        }
                    }),
                isDisabled: !Boolean(selectedState),
                isClearable: true,
                isLoading: fetchingLocations,
                className: 'grid-col-100'
            },
            {
                component: componentTypes.SELECT,
                name: 'types',
                label: 'Activity Types',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                className: 'grid-col-100',
                options: ActivityType,
                isMulti: true
            },
            {
                component: componentTypes.MONTH_PICKER,
                name: 'month',
                label: 'Month',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                className: 'grid-col-100',
                initialValue: exportData.month,
                minDate: getMinMonthYear(),
                maxDate: getMaxMonthYear(),
                range: true
            }
        ]
    };

    function getFileName() {
        const { parentCompany, associate_company, locations } = exportData;
        const _d = { ...parentCompany, ...associate_company, ...locations };
        const result = [
            _d.parentCompany.code
        ];
        if (_d.associate_company) {
            result.push(_d.associate_company.code);
        }
        if (_d.locations) {
            result.push(_d.locations.code);
        }
        return `${result.join('-')}.xlsx`
    }

    function debugForm(_form: any[]) {
        setForm(_form);
        setExportData(_form.values);
    }

    function onParentCompanyChange(e: any) {
        setParentCompany(e);
        setAssociateCompany(null);
        setSelectedState(null);
        setExportData({ ...exportData, parentCompany: e, associate_company: null, state: null, locations: null });
        setFormKey((k: number) => k + 1);
    }

    function onAssociateCompanyChange(e: any) {
        setAssociateCompany(e);
        setSelectedState(null);
        setExportData({ ...exportData, associate_company: e, state: null, locations: null });
        setFormKey((k: number) => k + 1);
    }
    
    function handleSubmit() {
        if (form.valid) {
            const { parentCompany, associate_company, locations, month, types } = exportData;
            let fromDate, toDate;
            if (Array.isArray(month)) {
                fromDate = new Date(month[0]);
                fromDate.setDate(1);
                toDate = new Date(month[1] || month[0]);
                toDate.setDate(1);
            } else {
                fromDate = new Date(month);
                fromDate.setDate(1);
                toDate = new Date(month);
                toDate.setDate(1);
            }
            const payload = {
                company_id: parentCompany.value,
                associate_company_id: (associate_company || {}).value || '',
                location_id: (locations || {}).value || '',
                from_date: dayjs(new Date(fromDate)).local().format(),
                to_date: dayjs(new Date(toDate)).local().format(),
                types: types.map((x: { value: any; }) => x.value).join(API_DELIMITER)
            };
            exportComplianceSchedule(payload);
        }
    }

    function resetForm() {
        setExportData({ ...DEFAULT_EXPORT_DATA });
    }

    const importSchema = {
            fields: [
                {
                    component: componentTypes.FILE_UPLOAD,
                    label: 'Upload File',
                    name: 'file',
                    type: 'file',
                    validate: [
                        { type: validatorTypes.REQUIRED },
                        { type: 'file-type', regex: EXCEL_FILE_REGEX },
                        { type: 'file-size', maxSize: 10 * FILE_SIZE.MB }
                    ]
                },
            ],
        };

    return (
        <>
            <MastersLayout title="Compliance Schedule" breadcrumbs={breadcrumb}>
                <div className="d-flex flex-column mx-0 mt-4">
                    <div className="d-flex flex-row justify-content-center mb-4">
                        <div className="w-50 px-4 border-end" style={{ maxWidth: '550px' }}>
                            <div className="d-flex flex-column card shadow p-4">
                                <div className="text-lg fw-bold">Export Compliance Schedule</div>
                                <div className="d-flex flex-column h-100 justify-space-between py-2 horizontal-form col2">
                                    <FormRenderer key={formKey} FormTemplate={FormTemplate}
                                        initialValues={exportData}
                                        componentMapper={ComponentMapper}
                                        schema={schema}
                                        debug={debugForm}
                                    />
                                </div>
                                <div className="d-flex justify-content-end mt-4 me-4">
                                    <Button variant="outline-secondary" className="btn btn-outline-secondary px-4" onClick={resetForm}>{'Reset'}</Button>
                                    <Button variant="primary" onClick={handleSubmit} className="ms-4 px-4" disabled={!form.valid}>{'Export'}</Button>
                                </div>
                            </div>
                        </div>
                        <div className="w-100 px-4" style={{ maxWidth: "calc(100% - 550px)", minWidth: "50%" }}>
                            <div className="d-flex flex-column">
                                <div className="d-flex flex-column card shadow p-4 mb-4">
                                    <div className="text-lg fw-bold mb-4">Import Compliance Schedule</div>
                                    <ul>
                                        <li>Click on Import Button</li>
                                        <li>Select the file and upload</li>
                                        <li>If there are errors, click on the hyper link and the application will export the errors in excel file for you to edit and re-upload</li>
                                    </ul>
                                    <div>
                                        <Button variant="primary" onClick={() => setImportFile(true)} className="px-4">{'Import'}</Button>
                                    </div>
                                </div>
                                <div className="d-flex flex-column card shadow p-4">
                                    <div className="text-lg fw-bold mb-4">Instructions for Export</div>
                                    <ul>
                                        <li>Select Company</li>
                                        <li>Select Associate Company (Optional)</li>
                                        <li>Select State (Optional) — filters the available Locations to only those in the selected State</li>
                                        <li>Select Location (Optional) — Location can only be selected after choosing a State</li>
                                        <li>Please note that if you are not selecting the Associate Company, State or Location, then the application will export all data for all Associate Companies or Locations.</li>
                                        <li>Select Month & Year. Here you can select the range of months to create the excel export</li>
                                        <li>Click Export button to export the data into an excel sheet.</li>
                                    </ul>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </MastersLayout>
            {/* {
                importFile && <ComplianceScheduleImportModal onClose={() => setImportFile(false)} />
            } */}
            {
                importFile && <BaseImportModal onClose={() => setImportFile(false)} importSchema={importSchema} importFunction={useImportComplianceSchedule} title="Import Compliance Schedule" />
            }
            {
                exporting && <PageLoader>Generating Audit Schedule...</PageLoader>
            }
        </>
    )
}

export default ComplianceSchedule;